import { useState } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';

// Statuts que l'on peut rejouer : la passerelle n'a pas transmis la requête au
// serveur, ou celui-ci n'a rien répondu du tout. Un 4xx est déterministe (le
// rejouer redonnerait la même réponse) et un 401 appartient à l'intercepteur,
// qui a déjà son propre rejeu après rafraîchissement du jeton
// (src/interceptors/axios.js).
const STATUTS_REJOUABLES = new Set([502, 503, 504]);

// Attentes de base entre deux essais, en millisecondes.
const ATTENTES_MS = [800, 2400];

const attendre = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// Tirage aléatoire de ±50 % sur l'attente. Ce n'est pas cosmétique : une classe
// entière part au même instant (le prof lance le quiz pour tous à la fois), donc
// elle échoue au même instant. Des réessais synchronisés recréeraient le pic qui
// vient de faire échouer l'envoi.
const brouiller = (ms) => Math.round(ms * (0.5 + Math.random()));

export const usePostApi = (initialUrl, initialData) => {
  const [data, setData] = useState(initialData);
  const [url, setUrl] = useState(initialUrl);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);

  /**
   * Envoie `postData` et renvoie TOUJOURS `{ success, data }` ou
   * `{ success: false, error }`. C'est ce retour qui fait foi : les états
   * `isError` / `isLoading` ne sont lisibles qu'au rendu suivant, jamais dans la
   * closure qui vient d'appeler `doPost`.
   *
   * @param options.tentatives  nombre total d'essais (1 par défaut : aucun
   *   appelant existant ne change de comportement). Ne le monter que pour un
   *   endpoint IDEMPOTENT — `defis-quotidiens/submit/` ne l'est pas, il crée une
   *   ligne neuve et crédite l'XP à chaque appel.
   * @param options.timeoutMs   délai d'attente par essai. Sans lui, axios attend
   *   indéfiniment (`axios.defaults.timeout` vaut 0) : une connexion pendue ne
   *   rejette jamais et aucun réessai ne partirait.
   */
  const doPost = async (postData, overrideUrl = url, options = {}) => {
    const { tentatives = 1, timeoutMs } = options;
    const nbEssais = Math.max(1, tentatives);

    setIsError(false);
    setIsLoading(true);
    const csrfToken = Cookies.get('csrftoken');
    const headers = {
      'X-CSRFToken': csrfToken,
    };

    if (!(postData instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }

    const config = { headers, withCredentials: true };
    if (timeoutMs) config.timeout = timeoutMs;

    let dernierEchec = 'Erreur lors de la requête';

    for (let essai = 1; essai <= nbEssais; essai += 1) {
      try {
        const result = await axios.post(overrideUrl, postData, config);

        if ((result.status === 201) || (result.status === 200)) {
          setData(result.data);
          setIsLoading(false);
          return { success: true, data: result.data };
        }
        // Statut inattendu mais réponse reçue : le serveur a tranché, on ne
        // rejoue pas.
        setIsError(true);
        setIsLoading(false);
        return { success: false, error: 'Erreur lors de la requête' };
      } catch (error) {
        dernierEchec = error;
        const statut = error.response?.status;
        // Pas de réponse du tout (coupure réseau, délai dépassé) ou passerelle
        // en défaut : c'est un raté de transport, il vaut la peine d'être rejoué.
        const rejouable = statut === undefined || STATUTS_REJOUABLES.has(statut);
        if (!rejouable || essai >= nbEssais) break;
        await attendre(brouiller(ATTENTES_MS[Math.min(essai - 1, ATTENTES_MS.length - 1)]));
      }
    }

    setIsError(true);
    setIsLoading(false);
    return { success: false, error: dernierEchec };
  };

  return [{ data, isLoading, isError }, doPost, setUrl];
};
